import { isIsoDuration } from '@core/filters/path-filter/dsl/parser';
import {
  FieldPath,
  FilterExpr,
  FilterLiteral,
  FilterOpName,
  FilterPredicate,
  LiteralRange,
  literalKind,
  opArgument,
  opName
} from '@core/filters/path-filter/wire';
import { foldForSearch } from '@core/utils/search-text.utils';
import { parseISO8601Duration } from '@core/utils/time-utils';

/** `null`: the node cannot be judged here, which never hides a row on its own. */
type Verdict = boolean | null;

/**
 * Evaluates a Path tree on rows held in the browser, for grids whose rows the backend never sees.
 * Like `makeFilterPredicate`, anything it cannot evaluate keeps the row; unlike a two-valued
 * evaluator, `not` of such a node stays unknown instead of hiding the row. Durations compare in
 * milliseconds, so `read` must return a duration cell in milliseconds.
 */
export function makePathFilterPredicate<T>(
  expr: FilterExpr | undefined,
  read: (row: T, path: FieldPath) => unknown
): (row: T) => boolean {
  if (!expr) return () => true;
  return (row) => evaluate(expr, (path) => read(row, path)) !== false;
}

function evaluate(expr: FilterExpr, read: (path: FieldPath) => unknown): Verdict {
  if ('and' in expr)
    return combine(
      expr.and.map((item) => evaluate(item, read)),
      false
    );
  if ('or' in expr)
    return combine(
      expr.or.map((item) => evaluate(item, read)),
      true
    );
  if ('not' in expr) {
    const inner = evaluate(expr.not, read);
    return inner === null ? null : !inner;
  }
  if ('association' in expr) return null;
  try {
    return predicate(expr.predicate, read(expr.predicate.path));
  } catch {
    return null;
  }
}

/** `decisive` settles the group on its own: `false` for an AND, `true` for an OR. */
function combine(verdicts: Verdict[], decisive: boolean): Verdict {
  if (verdicts.includes(decisive)) return decisive;
  return verdicts.includes(null) ? null : !decisive;
}

function scalar(literal: FilterLiteral): number | string | boolean | null {
  if (literal === 'null') return null;
  if ('number' in literal) return literal.number;
  if ('integer' in literal) return literal.integer;
  if ('string' in literal) return literal.string;
  if ('uid' in literal) return literal.uid;
  if ('bool' in literal) return literal.bool;
  // `parseISO8601Duration` reads an empty text as zero, which would make a half-typed bound filter.
  if ('duration' in literal && isIsoDuration(literal.duration)) {
    return parseISO8601Duration(literal.duration).totalMilliseconds;
  }
  throw new Error('literal not compared in the browser');
}

function compare(value: unknown, literal: FilterLiteral): number {
  const target = scalar(literal);
  if (typeof target === 'number' && typeof value === 'number') return value - target;
  if (typeof target === 'string' && typeof value === 'string') {
    return value.localeCompare(target);
  }
  throw new Error('values of different kinds');
}

/**
 * Text compares in any case and with or without accents, as `makeFilterPredicate` does; uids and
 * other kinds stay exact.
 */
function same(value: unknown, literal: FilterLiteral): boolean {
  const target = scalar(literal);
  if (literalKind(literal) === 'string' && typeof value === 'string') {
    return foldForSearch(value) === foldForSearch(target as string);
  }
  return target === value;
}

function text(value: unknown): string {
  return foldForSearch(value == null ? '' : String(value));
}

const ORDERINGS: ReadonlySet<FilterOpName> = new Set<FilterOpName>([
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'between'
]);

function predicate({ op }: FilterPredicate, value: unknown): Verdict {
  const name = opName(op);
  const arg = opArgument(op);
  const empty = value == null || value === '';
  // An empty cell is a known value that no bound holds, as in `makeFilterPredicate`.
  if (empty && ORDERINGS.has(name)) return false;
  switch (name) {
    case 'is_empty':
      return empty;
    case 'is_not_empty':
      return !empty;
    case 'equals':
      return same(value, arg as FilterLiteral);
    case 'not_equals':
      return !same(value, arg as FilterLiteral);
    case 'greater_than':
      return compare(value, arg as FilterLiteral) > 0;
    case 'greater_than_or_equal':
      return compare(value, arg as FilterLiteral) >= 0;
    case 'less_than':
      return compare(value, arg as FilterLiteral) < 0;
    case 'less_than_or_equal':
      return compare(value, arg as FilterLiteral) <= 0;
    case 'between': {
      const range = arg as LiteralRange;
      return compare(value, range.lo) >= 0 && compare(value, range.hi) <= 0;
    }
    case 'is_any_of':
      return (arg as FilterLiteral[]).some((literal) => same(value, literal));
    case 'is_none_of':
      return !(arg as FilterLiteral[]).some((literal) => same(value, literal));
    case 'contains':
      return text(value).includes(text(arg));
    case 'not_contains':
      return !text(value).includes(text(arg));
    case 'starts_with':
      return text(value).startsWith(text(arg));
    case 'ends_with':
      return text(value).endsWith(text(arg));
    default:
      return null;
  }
}
